import { formatDate as formatDateUtil, formatDateTime as formatDateTimeUtil } from '../../utils/dateUtils';
import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
    ArrowLeft,
    Edit,
    Car,
    Home,
    DollarSign,
    MapPin,
    Tag,
    Image as ImageIcon,
    Calendar,
    Store,
    CheckCircle,
    XCircle,
    Clock,
    User,
    AlertCircle,
    ChevronLeft,
    ChevronRight,
    X
} from 'lucide-react';
import { useGetMarketplaceListingQuery } from '../../store/slices/apiSlice';
import { Button } from '../../components/ui/Button';
import { Badge } from '../../components/ui/Badge';
import { Modal } from '../../components/ui/Modal';

const ViewMarketplaceListing: React.FC = () => {
    const navigate = useNavigate();
    const { id } = useParams<{ id: string }>();
    const listingId = id ? parseInt(id) : null;
    const isValidId = listingId && !isNaN(listingId) && listingId > 0;

    const [selectedImageIndex, setSelectedImageIndex] = useState<number | null>(null);
    const [showImageModal, setShowImageModal] = useState(false);

    const { data: listingResponse, isLoading, error } = useGetMarketplaceListingQuery(listingId!, {
        skip: !isValidId,
        refetchOnMountOrArgChange: true
    });

    const listing = listingResponse?.data;

    const formatCurrency = (amount: number, symbol: string | null = null) => {
        const currencySymbol = symbol || '₦';
        return `${currencySymbol}${amount.toLocaleString('en-US')}`;
    };

    const handleImageClick = (index: number) => {
        setSelectedImageIndex(index);
        setShowImageModal(true);
    };

    const handleNextImage = () => {
        if (listing?.images && selectedImageIndex !== null) {
            setSelectedImageIndex((selectedImageIndex + 1) % listing.images.length);
        }
    };

    const handlePrevImage = () => {
        if (listing?.images && selectedImageIndex !== null) {
            setSelectedImageIndex((selectedImageIndex - 1 + listing.images.length) % listing.images.length);
        }
    };

    const handleCloseModal = () => {
        setShowImageModal(false);
        setSelectedImageIndex(null);
    };

    // Keyboard navigation
    useEffect(() => {
        if (!showImageModal || !listing?.images) return;

        const handleKeyPress = (e: KeyboardEvent) => {
            if (e.key === 'Escape') {
                handleCloseModal();
            } else if (e.key === 'ArrowLeft' && selectedImageIndex !== null) {
                setSelectedImageIndex((selectedImageIndex - 1 + listing.images.length) % listing.images.length);
            } else if (e.key === 'ArrowRight' && selectedImageIndex !== null) {
                setSelectedImageIndex((selectedImageIndex + 1) % listing.images.length);
            }
        };

        window.addEventListener('keydown', handleKeyPress);
        return () => window.removeEventListener('keydown', handleKeyPress);
    }, [showImageModal, selectedImageIndex, listing?.images]);

    const getStatusBadge = (status: string) => {
        switch (status) {
            case 'active':
                return <Badge variant="success" className="flex items-center gap-1"><CheckCircle className="w-3 h-3" />Active</Badge>;
            case 'sold':
                return <Badge variant="default" className="flex items-center gap-1"><CheckCircle className="w-3 h-3" />Sold</Badge>;
            case 'draft':
                return <Badge variant="default" className="flex items-center gap-1"><Clock className="w-3 h-3" />Draft</Badge>;
            case 'inactive':
                return <Badge variant="danger" className="flex items-center gap-1"><XCircle className="w-3 h-3" />Inactive</Badge>;
            default:
                return <Badge variant="default">{status}</Badge>;
        }
    };

    if (!isValidId) {
        return (
            <div className="p-6">
                <div className="bg-red-50 border border-red-200 rounded-lg p-6">
                    <p className="text-red-800">Invalid listing ID</p>
                </div>
            </div>
        );
    }

    if (isLoading) {
        return (
            <div className="p-6">
                <div className="flex items-center justify-center h-64">
                    <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
                </div>
            </div>
        );
    }

    if (error || !listing) {
        return (
            <div className="p-6">
                <div className="bg-red-50 border border-red-200 rounded-lg p-6">
                    <p className="text-red-800">Error loading listing. Please try again.</p>
                    <button
                        onClick={() => navigate('/admin/marketplace-listings')}
                        className="mt-4 px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700"
                    >
                        Go Back
                    </button>
                </div>
            </div>
        );
    }

    return (
        <div className="min-h-screen bg-gray-50">
            {/* Header */}
            <div className="bg-white border-b border-gray-200">
                <div className="px-6 py-4">
                    <div className="flex items-center justify-between">
                        <div className="flex items-center space-x-4">
                            <button
                                onClick={() => navigate('/admin/marketplace-listings')}
                                className="p-2 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-lg transition-colors"
                            >
                                <ArrowLeft className="w-5 h-5" />
                            </button>
                            <div>
                                <h1 className="text-2xl font-bold text-gray-900">View Marketplace Listing</h1>
                                <p className="text-gray-600">Listing details and information</p>
                            </div>
                        </div>
                        <Button
                            onClick={() => navigate(`/admin/marketplace-listings/edit/${listing.listing_id}`)}
                            variant="primary"
                        >
                            <Edit className="w-4 h-4 mr-2" />
                            Edit Listing
                        </Button>
                    </div>
                </div>
            </div>

            <div className="px-6 py-6">
                <div className="max-w-6xl mx-auto space-y-6">
                    {/* Basic Information */}
                    <div className="bg-white rounded-lg shadow">
                        <div className="px-6 py-4 border-b border-gray-200">
                            <h2 className="text-lg font-semibold text-gray-900 flex items-center">
                                <Store className="w-5 h-5 mr-2" />
                                Basic Information
                            </h2>
                        </div>
                        <div className="p-6 grid grid-cols-1 md:grid-cols-2 gap-6">
                            <div>
                                <label className="text-sm font-medium text-gray-700">Listing Type</label>
                                <div className="mt-1 flex items-center gap-2">
                                    {listing.listing_type === 'car' ? (
                                        <Car className="w-5 h-5 text-blue-600" />
                                    ) : (
                                        <Home className="w-5 h-5 text-blue-600" />
                                    )}
                                    <span className="text-sm text-gray-900 capitalize">{listing.listing_type}</span>
                                </div>
                            </div>
                            <div>
                                <label className="text-sm font-medium text-gray-700">Status</label>
                                <div className="mt-1">{getStatusBadge(listing.status)}</div>
                            </div>
                            <div className="md:col-span-2">
                                <label className="text-sm font-medium text-gray-700">Title</label>
                                <p className="mt-1 text-sm text-gray-900">{listing.title}</p>
                            </div>
                            <div className="md:col-span-2">
                                <label className="text-sm font-medium text-gray-700">Description</label>
                                <p className="mt-1 text-sm text-gray-900 whitespace-pre-wrap">{listing.description}</p>
                            </div>
                            <div>
                                <label className="text-sm font-medium text-gray-700">Price</label>
                                <p className="mt-1 text-lg font-semibold text-gray-900">
                                    {formatCurrency(listing.price, listing.currency_symbol)}
                                </p>
                            </div>
                            <div>
                                <label className="text-sm font-medium text-gray-700">Location</label>
                                <div className="mt-1 flex items-center gap-2">
                                    <MapPin className="w-4 h-4 text-gray-400" />
                                    <p className="text-sm text-gray-900">{listing.location || 'N/A'}</p>
                                </div>
                            </div>
                            {listing.tags && listing.tags.length > 0 && (
                                <div className="md:col-span-2">
                                    <label className="text-sm font-medium text-gray-700">Tags</label>
                                    <div className="mt-1 flex flex-wrap gap-2">
                                        {listing.tags.map((tag, index) => (
                                            <span
                                                key={index}
                                                className="inline-flex items-center px-3 py-1 rounded-full text-sm bg-blue-100 text-blue-800"
                                            >
                                                <Tag className="w-3 h-3 mr-1" />
                                                {tag}
                                            </span>
                                        ))}
                                    </div>
                                </div>
                            )}
                            <div>
                                <label className="text-sm font-medium text-gray-700">Featured</label>
                                <div className="mt-1">
                                    {listing.is_featured ? (
                                        <Badge variant="success">Featured</Badge>
                                    ) : (
                                        <span className="text-sm text-gray-500">Not Featured</span>
                                    )}
                                </div>
                            </div>
                            <div>
                                <label className="text-sm font-medium text-gray-700">Created At</label>
                                <div className="mt-1 flex items-center gap-2">
                                    <Calendar className="w-4 h-4 text-gray-400" />
                                    <p className="text-sm text-gray-900">
                                        {formatDateTimeUtil(listing.created_at)}
                                    </p>
                                </div>
                            </div>
                        </div>
                    </div>

                    {/* Dealer Information */}
                    {(listing.dealer || listing.created_by) && (
                        <div className="bg-white rounded-lg shadow">
                            <div className="px-6 py-4 border-b border-gray-200">
                                <h2 className="text-lg font-semibold text-gray-900 flex items-center">
                                    <User className="w-5 h-5 mr-2" />
                                    {listing.listing_source === 'dealer' ? 'Dealer Information' : 'Creator Information'}
                                </h2>
                            </div>
                            <div className="p-6">
                                {listing.listing_source === 'dealer' && listing.dealer ? (
                                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                                        <div>
                                            <label className="text-sm font-medium text-gray-700">Dealer Name</label>
                                            <p className="mt-1 text-sm text-gray-900">{listing.dealer.name}</p>
                                        </div>
                                        <div>
                                            <label className="text-sm font-medium text-gray-700">Business Name</label>
                                            <p className="mt-1 text-sm text-gray-900">{listing.dealer.business_name || 'N/A'}</p>
                                        </div>
                                        <div>
                                            <label className="text-sm font-medium text-gray-700">Email</label>
                                            <p className="mt-1 text-sm text-gray-900">{listing.dealer.email}</p>
                                        </div>
                                        <div>
                                            <label className="text-sm font-medium text-gray-700">Phone</label>
                                            <p className="mt-1 text-sm text-gray-900">
                                                {listing.dealer.mobile_number || listing.dealer.business_phone || 'N/A'}
                                            </p>
                                        </div>
                                        <div className="md:col-span-2">
                                            <label className="text-sm font-medium text-gray-700">Dealer ID</label>
                                            <p className="mt-1 text-sm text-gray-900">#{listing.dealer.dealer_id}</p>
                                        </div>
                                    </div>
                                ) : (
                                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                                        <div>
                                            <label className="text-sm font-medium text-gray-700">Created By</label>
                                            <p className="mt-1 text-sm text-gray-900">Admin: {listing.created_by?.name || 'System'}</p>
                                        </div>
                                        <div>
                                            <label className="text-sm font-medium text-gray-700">Admin Email</label>
                                            <p className="mt-1 text-sm text-gray-900">{listing.created_by?.email || 'N/A'}</p>
                                        </div>
                                    </div>
                                )}
                            </div>
                        </div>
                    )}

                    {/* Car Details */}
                    {listing.listing_type === 'car' && listing.car_details && (
                        <div className="bg-white rounded-lg shadow">
                            <div className="px-6 py-4 border-b border-gray-200">
                                <h2 className="text-lg font-semibold text-gray-900 flex items-center">
                                    <Car className="w-5 h-5 mr-2" />
                                    Car Details
                                </h2>
                            </div>
                            <div className="p-6 grid grid-cols-1 md:grid-cols-2 gap-6">
                                {listing.car_details.vin && (
                                    <div>
                                        <label className="text-sm font-medium text-gray-700">VIN Number</label>
                                        <p className="mt-1 text-sm text-gray-900 font-mono">{listing.car_details.vin}</p>
                                    </div>
                                )}
                                {listing.car_details.make && (
                                    <div>
                                        <label className="text-sm font-medium text-gray-700">Make</label>
                                        <p className="mt-1 text-sm text-gray-900">{listing.car_details.make}</p>
                                    </div>
                                )}
                                {listing.car_details.model && (
                                    <div>
                                        <label className="text-sm font-medium text-gray-700">Model</label>
                                        <p className="mt-1 text-sm text-gray-900">{listing.car_details.model}</p>
                                    </div>
                                )}
                                {listing.car_details.year && (
                                    <div>
                                        <label className="text-sm font-medium text-gray-700">Year</label>
                                        <p className="mt-1 text-sm text-gray-900">{listing.car_details.year}</p>
                                    </div>
                                )}
                                {listing.car_details.mileage && (
                                    <div>
                                        <label className="text-sm font-medium text-gray-700">Mileage</label>
                                        <p className="mt-1 text-sm text-gray-900">{listing.car_details.mileage}</p>
                                    </div>
                                )}
                            </div>
                        </div>
                    )}

                    {/* House Details */}
                    {listing.listing_type === 'house' && listing.house_details && (
                        <div className="bg-white rounded-lg shadow">
                            <div className="px-6 py-4 border-b border-gray-200">
                                <h2 className="text-lg font-semibold text-gray-900 flex items-center">
                                    <Home className="w-5 h-5 mr-2" />
                                    House Details
                                </h2>
                            </div>
                            <div className="p-6 grid grid-cols-1 md:grid-cols-2 gap-6">
                                {listing.house_details.bedrooms && (
                                    <div>
                                        <label className="text-sm font-medium text-gray-700">Bedrooms</label>
                                        <p className="mt-1 text-sm text-gray-900">{listing.house_details.bedrooms}</p>
                                    </div>
                                )}
                                {listing.house_details.bathrooms && (
                                    <div>
                                        <label className="text-sm font-medium text-gray-700">Bathrooms</label>
                                        <p className="mt-1 text-sm text-gray-900">{listing.house_details.bathrooms}</p>
                                    </div>
                                )}
                                {listing.house_details.square_feet && (
                                    <div>
                                        <label className="text-sm font-medium text-gray-700">Square Feet</label>
                                        <p className="mt-1 text-sm text-gray-900">{listing.house_details.square_feet}</p>
                                    </div>
                                )}
                                {listing.house_details.property_type && (
                                    <div>
                                        <label className="text-sm font-medium text-gray-700">Property Type</label>
                                        <p className="mt-1 text-sm text-gray-900">{listing.house_details.property_type}</p>
                                    </div>
                                )}
                            </div>
                        </div>
                    )}

                    {/* Images */}
                    {listing.images && listing.images.length > 0 && (
                        <div className="bg-white rounded-lg shadow">
                            <div className="px-6 py-4 border-b border-gray-200">
                                <h2 className="text-lg font-semibold text-gray-900 flex items-center">
                                    <ImageIcon className="w-5 h-5 mr-2" />
                                    Images ({listing.images.length})
                                </h2>
                            </div>
                            <div className="p-6">
                                <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
                                    {listing.images.map((image, index) => (
                                        <div 
                                            key={image.image_id} 
                                            className="relative group cursor-pointer"
                                            onClick={() => handleImageClick(index)}
                                        >
                                            <div className="aspect-square rounded-lg overflow-hidden bg-gray-100 border-2 border-transparent group-hover:border-blue-500 transition-all">
                                                <img
                                                    src={image.image_path}
                                                    alt={`${listing.title} - Image ${index + 1}`}
                                                    className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
                                                />
                                            </div>
                                            {image.is_primary && (
                                                <span className="absolute top-2 left-2 px-2 py-1 bg-blue-600 text-white text-xs rounded shadow-lg">
                                                    Primary
                                                </span>
                                            )}
                                            <div className="absolute inset-0 bg-black bg-opacity-0 group-hover:bg-opacity-10 transition-all rounded-lg flex items-center justify-center">
                                                <ImageIcon className="w-8 h-8 text-white opacity-0 group-hover:opacity-100 transition-opacity" />
                                            </div>
                                        </div>
                                    ))}
                                </div>
                                <p className="mt-4 text-sm text-gray-500 text-center">Click on any image to view in full size</p>
                            </div>
                        </div>
                    )}
                </div>
            </div>

            {/* Image Modal */}
            {showImageModal && selectedImageIndex !== null && listing?.images && (
                <div className="fixed inset-0 z-50 bg-black bg-opacity-90 flex items-center justify-center p-4">
                    <div className="relative max-w-7xl max-h-full w-full h-full flex items-center justify-center">
                        {/* Close Button */}
                        <button
                            onClick={handleCloseModal}
                            className="absolute top-4 right-4 z-10 p-2 bg-white bg-opacity-20 hover:bg-opacity-30 rounded-full text-white transition-all"
                        >
                            <X className="w-6 h-6" />
                        </button>

                        {/* Previous Button */}
                        {listing.images.length > 1 && (
                            <button
                                onClick={handlePrevImage}
                                className="absolute left-4 z-10 p-3 bg-white bg-opacity-20 hover:bg-opacity-30 rounded-full text-white transition-all"
                            >
                                <ChevronLeft className="w-8 h-8" />
                            </button>
                        )}

                        {/* Image */}
                        <div className="relative max-w-full max-h-full flex items-center justify-center">
                            <img
                                src={listing.images[selectedImageIndex].image_path}
                                alt={`${listing.title} - Image ${selectedImageIndex + 1}`}
                                className="max-w-full max-h-[90vh] object-contain rounded-lg"
                            />
                        </div>

                        {/* Next Button */}
                        {listing.images.length > 1 && (
                            <button
                                onClick={handleNextImage}
                                className="absolute right-4 z-10 p-3 bg-white bg-opacity-20 hover:bg-opacity-30 rounded-full text-white transition-all"
                            >
                                <ChevronRight className="w-8 h-8" />
                            </button>
                        )}

                        {/* Image Counter */}
                        <div className="absolute bottom-4 left-1/2 transform -translate-x-1/2 px-4 py-2 bg-black bg-opacity-50 rounded-full text-white text-sm">
                            {selectedImageIndex + 1} / {listing.images.length}
                        </div>

                        {/* Thumbnail Strip */}
                        {listing.images.length > 1 && (
                            <div className="absolute bottom-16 left-1/2 transform -translate-x-1/2 flex gap-2 max-w-full overflow-x-auto px-4">
                                {listing.images.map((image, index) => (
                                    <button
                                        key={image.image_id}
                                        onClick={() => setSelectedImageIndex(index)}
                                        className={`flex-shrink-0 w-16 h-16 rounded-lg overflow-hidden border-2 transition-all ${
                                            index === selectedImageIndex
                                                ? 'border-blue-500 ring-2 ring-blue-300'
                                                : 'border-transparent opacity-60 hover:opacity-100'
                                        }`}
                                    >
                                        <img
                                            src={image.image_path}
                                            alt={`Thumbnail ${index + 1}`}
                                            className="w-full h-full object-cover"
                                        />
                                    </button>
                                ))}
                            </div>
                        )}
                    </div>
                </div>
            )}
        </div>
    );
};

export default ViewMarketplaceListing;

